<template>
  <div class="page-container">
    <el-card class="search-card">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索用户名、昵称或邮箱" 
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch">
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-select v-model="statusFilter" placeholder="状态筛选" clearable @change="handleSearch">
            <el-option label="全部" :value="null"></el-option>
            <el-option label="正常" :value="0"></el-option>
            <el-option label="禁用" :value="1"></el-option>
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-button @click="handleReset" icon="el-icon-refresh">重置</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card>
      <el-table :data="tableData" border v-loading="loading">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column label="头像" width="80">
          <template slot-scope="scope">
            <el-avatar :src="scope.row.avatar" icon="el-icon-user-solid"></el-avatar>
          </template>
        </el-table-column>
        <el-table-column prop="username" label="用户名" width="120"></el-table-column>
        <el-table-column prop="nickname" label="昵称" width="120"></el-table-column>
        <el-table-column prop="email" label="邮箱" width="180"></el-table-column>
        <el-table-column prop="points" label="积分" width="100">
          <template slot-scope="scope">
            <el-tag type="warning" size="small">{{ scope.row.points || 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="themePreference" label="主题偏好" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.themePreference === 'light'" type="success" size="small">浅色</el-tag>
            <el-tag v-else-if="scope.row.themePreference === 'dark'" type="info" size="small">深色</el-tag>
            <el-tag v-else size="small">跟随系统</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 0" type="success">正常</el-tag>
            <el-tag v-else type="danger">禁用</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="注册时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="300" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleEditPoints(scope.row)">修改积分</el-button>
            <el-button size="mini" type="warning" @click="handleResetPassword(scope.row)">重置密码</el-button>
            <el-button 
              size="mini" 
              :type="scope.row.status === 0 ? 'danger' : 'success'" 
              @click="handleToggleStatus(scope.row)">
              {{ scope.row.status === 0 ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :current-page="page.current"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="page.total"
        style="margin-top: 20px; text-align: right;">
      </el-pagination>
    </el-card>

    <!-- 修改积分对话框 -->
    <el-dialog title="修改用户积分" :visible.sync="pointsDialogVisible" width="400px">
      <div style="margin-bottom: 15px;">
        <strong>用户：</strong>{{ currentUser.nickname }} ({{ currentUser.username }})
      </div>
      <div style="margin-bottom: 15px;">
        <strong>当前积分：</strong><el-tag type="warning">{{ currentUser.points || 0 }}</el-tag>
      </div>
      <el-form label-width="80px">
        <el-form-item label="新积分">
          <el-input-number v-model="newPoints" :min="0" :max="999999" style="width: 100%;"></el-input-number>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="pointsDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSavePoints" :loading="savingPoints">保存</el-button>
      </span>
    </el-dialog>

    <!-- 重置密码对话框 -->
    <el-dialog title="重置用户密码" :visible.sync="passwordDialogVisible" width="400px">
      <div style="margin-bottom: 15px;">
        <strong>用户：</strong>{{ currentUser.nickname }} ({{ currentUser.username }})
      </div>
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="80px">
        <el-form-item label="新密码" prop="newPassword">
          <el-input type="password" v-model="passwordForm.newPassword" placeholder="请输入新密码（6-20位）"></el-input>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input type="password" v-model="passwordForm.confirmPassword" placeholder="请再次输入新密码"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSavePassword" :loading="savingPassword">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'FrontUserManage',
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请再次输入密码'))
      } else if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入密码不一致!'))
      } else {
        callback()
      }
    }
    
    return {
      tableData: [],
      loading: false,
      searchKeyword: '',
      statusFilter: null,
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      pointsDialogVisible: false,
      passwordDialogVisible: false,
      currentUser: {},
      newPoints: 0,
      savingPoints: false,
      savingPassword: false,
      passwordForm: {
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await this.$axios.get('/admin/front-users/page', {
          params: {
            current: this.page.current,
            size: this.page.size,
            keyword: this.searchKeyword,
            status: this.statusFilter
          }
        })
        if (res.data.code === 200) {
          this.tableData = res.data.data.records
          this.page.total = res.data.data.total
        }
      } catch (error) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.loadData()
    },
    handleReset() {
      this.searchKeyword = ''
      this.statusFilter = null
      this.page.current = 1
      this.loadData()
    },
    handlePageChange(page) {
      this.page.current = page
      this.loadData()
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.loadData()
    },
    handleEditPoints(row) {
      this.currentUser = row
      this.newPoints = row.points || 0
      this.pointsDialogVisible = true
    },
    async handleSavePoints() {
      this.savingPoints = true
      try {
        await this.$axios.put(`/admin/front-users/${this.currentUser.id}/points`, {
          points: this.newPoints
        })
        this.$message.success('积分修改成功')
        this.pointsDialogVisible = false
        this.loadData()
      } catch (error) {
        this.$message.error(error.response?.data?.message || '积分修改失败')
      } finally {
        this.savingPoints = false
      }
    },
    handleResetPassword(row) {
      this.currentUser = row
      this.passwordForm = {
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordDialogVisible = true
      this.$nextTick(() => {
        this.$refs.passwordForm && this.$refs.passwordForm.clearValidate()
      })
    },
    async handleSavePassword() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (!valid) return

        this.savingPassword = true
        try {
          await this.$axios.put(`/admin/front-users/${this.currentUser.id}/reset-password`, {
            newPassword: this.passwordForm.newPassword
          })
          this.$message.success('密码重置成功')
          this.passwordDialogVisible = false
        } catch (error) {
          this.$message.error(error.response?.data?.message || '密码重置失败')
        } finally {
          this.savingPassword = false
        }
      })
    },
    handleToggleStatus(row) {
      const newStatus = row.status === 0 ? 1 : 0
      const action = newStatus === 1 ? '禁用' : '启用'
      
      this.$confirm(`确定${action}用户"${row.nickname}"?`, '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          await this.$axios.put(`/admin/front-users/${row.id}/status`, {
            status: newStatus
          })
          this.$message.success(`${action}成功`)
          this.loadData()
        } catch (error) {
          this.$message.error(`${action}失败`)
        }
      }).catch(() => {})
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
  background: transparent;
  min-height: calc(100vh - 60px);
}

.search-card {
  margin-bottom: 20px;
}
</style>
